// src/pages/rh/ContratsExpires.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Row, Col, Table, Button, Badge, Spinner,
    Form, Pagination, Tooltip, OverlayTrigger, Alert
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";
import { formatDate } from "../../utils/formatters";

/* ============================================================
   TOAST
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    useEffect(() => {
        if (show && autoHideDelay) {
            const t = setTimeout(onClose, autoHideDelay);
            return () => clearTimeout(t);
        }
    }, [show, autoHideDelay, onClose]);

    if (!show) return null;
    const cfg = {
        success: { bg: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bg: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bg: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bg: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bg: COLORS.info, icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: cfg.bg }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${cfg.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: cfg.bg, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ContratsExpires() {
    const navigate = useNavigate();
    const [contrats, setContrats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterPeriode, setFilterPeriode] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => { fetchContrats(); }, []);

    const fetchContrats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/contrats.php?action=expires`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error("Erreur chargement");
            const data = await res.json();
            setContrats(data.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* --------- FILTRES --------- */
    const filtered = useMemo(() => {
        return contrats.filter(c => {
            const matchSearch = !searchTerm ||
                (c.employe_nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.employe_prenom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.employe_nom_ref || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.employe_prenom_ref || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.client_raison_sociale || '').toLowerCase().includes(searchTerm.toLowerCase());

            const matchType = filterType === 'all' || c.type_contrat === filterType;

            let matchPeriode = true;
            const jours = Number(c.jours_depuis_expiration) || 0;
            if (filterPeriode === 'recent')   matchPeriode = jours <= 7;
            if (filterPeriode === 'moyen')    matchPeriode = jours > 7 && jours <= 30;
            if (filterPeriode === 'ancien')   matchPeriode = jours > 30;

            return matchSearch && matchType && matchPeriode;
        });
    }, [contrats, searchTerm, filterType, filterPeriode]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filtered.slice(startIndex, startIndex + itemsPerPage);

    const resetFilters = () => {
        setSearchTerm('');
        setFilterType('all');
        setFilterPeriode('all');
        setCurrentPage(1);
    };

    /* --------- HELPERS --------- */
    const getExpirationLabel = (jours) => {
        const j = Number(jours) || 0;
        if (j <= 7)   return { text: `Il y a ${j} jour${j > 1 ? 's' : ''}`, color: COLORS.danger, icon: 'ri-error-warning-line' };
        if (j <= 30)  return { text: `Il y a ${j} jours`, color: COLORS.warning, icon: 'ri-time-line' };
        if (j <= 90)  return { text: `Il y a ${Math.floor(j / 30)} mois`, color: '#64748b', icon: 'ri-calendar-line' };
        return { text: `Il y a ${Math.floor(j / 30)} mois`, color: '#475569', icon: 'ri-history-line' };
    };

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    if (loading && contrats.length === 0) {
       
               return (
                   <>
                       <Header />
                       <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                           <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                               <div className="text-center" style={{ maxWidth: '400px' }}>
                                   <div className="loading-ring-wrapper mb-4">
                                       <div className="loading-ring"></div>
                                       <div className="loading-ring-inner"></div>
                                       <i className="ri-loader-4-fill loading-icon"></i>
                                   </div>
                                   <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                       Chargement des sauvegardes...
                                   </h5>
                                   <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                   <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                       <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                           <div
                                               className="progress-bar progress-bar-striped progress-bar-animated"
                                               style={{
                                                   width: '100%',
                                                   background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                   backgroundSize: '200% 100%',
                                                   animation: 'shimmer 1.5s linear infinite'
                                               }}
                                           />
                                       </div>
                                   </div>
                               </div>
                           </div>
                       </div>
                       <Footer />
                       <style>{`
                           .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                           .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                           .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                           .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                           @keyframes spin { to { transform: rotate(360deg); } }
                           @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                           @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                       `}</style>
                   </>
               );
           }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                <div className="container-fluid">
                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                <i className="ri-history-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Contrats expirés</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-list-line"></i> Total {contrats.length}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-error-warning-line"></i> Affichés {filtered.length}
                                    </span>
                                    {contrats.filter(c => Number(c.jours_depuis_expiration) <= 7).length > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                            <i className="ri-alarm-warning-line"></i> Récents (+7j)
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchContrats} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/rh/contrats')}>
                                <i className="ri-list-check"></i> Tous les contrats
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-history-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total expirés</span>
                                    <h3 className="kpi-value-charte">{contrats.length}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-alarm-warning-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">+7 derniers jours</span>
                                    <h3 className="kpi-value-charte">
                                        {contrats.filter(c => Number(c.jours_depuis_expiration) <= 7).length}
                                    </h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">8 à 30 jours</span>
                                    <h3 className="kpi-value-charte">
                                        {contrats.filter(c => Number(c.jours_depuis_expiration) > 7 && Number(c.jours_depuis_expiration) <= 30).length}
                                    </h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-timer-flash-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">+30 jours</span>
                                    <h3 className="kpi-value-charte">
                                        {contrats.filter(c => Number(c.jours_depuis_expiration) > 30).length}
                                    </h3>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par nom, référence, client..."
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select value={filterType} onChange={e => setFilterType(e.target.value)} className="filter-select-charte">
                                        <option value="all">Tous les types</option>
                                        <option value="CDD">CDD</option>
                                        <option value="CDI">CDI</option>
                                        <option value="AVENANT">Avenant</option>
                                        <option value="CERTIFICAT">Certificat</option>
                                    </Form.Select>
                                </Col>
                                <Col md="3">
                                    <Form.Select value={filterPeriode} onChange={e => setFilterPeriode(e.target.value)} className="filter-select-charte">
                                        <option value="all">Toutes les périodes</option>
                                        <option value="recent">7 derniers jours</option>
                                        <option value="moyen">8 à 30 jours</option>
                                        <option value="ancien">+30 jours</option>
                                    </Form.Select>
                                </Col>
                                <Col md="1" className="text-end">
                                    {(searchTerm || filterType !== 'all' || filterPeriode !== 'all') && (
                                        <Button onClick={resetFilters} className="btn-modern-light-outline">
                                            <i className="ri-close-line"></i>
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient" style={{ background: `linear-gradient(135deg, ${COLORS.danger} 0%, #dc2626 100%)` }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-history-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des contrats expirés</span>
                                    <span className="badge-header-light">{filtered.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Employé</th>
                                            <th>Réf.</th>
                                            <th>Client</th>
                                            <th className="text-center">Type</th>
                                            <th>Fin de contrat</th>
                                            <th className="text-center">Expiré depuis</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map(c => {
                                            const exp = getExpirationLabel(c.jours_depuis_expiration);
                                            return (
                                                <tr key={c.id}>
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="employee-avatar-charte">
                                                                {(c.employe_prenom_ref || c.employe_prenom || '?').charAt(0)}
                                                                {(c.employe_nom_ref || c.employe_nom || '?').charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                    {c.employe_prenom_ref || c.employe_prenom} {c.employe_nom_ref || c.employe_nom}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {c.employe_matricule || c.poste_qualification || 'N/A'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="reference-badge-charte">
                                                            <i className="ri-hashtag"></i>
                                                            {c.reference || `CT-${c.id}`}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted">{c.client_raison_sociale || '—'}</td>
                                                    <td className="text-center">
                                                        <span className="type-badge" style={{
                                                            background: c.type_contrat === 'CDD' ? `${COLORS.warning}15` :
                                                                        c.type_contrat === 'CDI' ? `${COLORS.info}15` : `${COLORS.primary}15`,
                                                            color: c.type_contrat === 'CDD' ? COLORS.warning :
                                                                   c.type_contrat === 'CDI' ? COLORS.info : COLORS.primary
                                                        }}>
                                                            {c.type_contrat || 'CDD'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="ri-calendar-close-line" style={{ color: COLORS.danger }}></i>
                                                            <span className="fw-semibold small">{formatDate(c.date_fin)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="expiration-badge" style={{ background: `${exp.color}15`, color: exp.color, border: `1.5px solid ${exp.color}40` }}>
                                                            <i className={exp.icon}></i>
                                                            {exp.text}
                                                        </span>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <ActionButton
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/rh/contrats/${c.id}`)}
                                                                tooltip="Voir détails"
                                                            />
                                                            <ActionButton
                                                                icon="ri-refresh-line"
                                                                onClick={() => navigate(`/rh/contratsNouveau?avenant=${c.id}`)}
                                                                tooltip="Créer un avenant"
                                                                color={COLORS.info}
                                                            />
                                                            <ActionButton
                                                                icon="ri-award-line"
                                                                onClick={() => navigate(`/rh/contratsNouveau?certificat=${c.id}`)}
                                                                tooltip="Certificat de travail"
                                                                color={COLORS.success}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-checkbox-circle-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun contrat expiré</h6>
                                                        <p className="text-muted small mb-0">
                                                            {searchTerm || filterType !== 'all' || filterPeriode !== 'all'
                                                                ? 'Essayez de modifier vos filtres'
                                                                : 'Tous vos contrats sont à jour'}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {totalPages > 1 && (
                                <div className="pagination-wrapper-charte">
                                    <div className="pagination-info-charte">
                                        Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong> — {filtered.length} contrat(s)
                                    </div>
                                    <Pagination className="pagination-charte mb-0">
                                        <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let p;
                                            if (totalPages <= 5) p = i + 1;
                                            else if (currentPage <= 3) p = i + 1;
                                            else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
                                            else p = currentPage - 2 + i;
                                            return <Pagination.Item key={i} active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>;
                                        })}
                                        <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }
                .header-modern { background: white; padding: 1.2rem 1.8rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid var(--danger); border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, var(--danger), #dc2626); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; box-shadow: 0 4px 12px rgba(239,68,68,0.25); }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern { background: #f1f4f9; color: #475569; padding: 0.3rem 0.8rem; border-radius: 50px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }
                .kpi-charte { background: white; border-radius: 12px; padding: 1.2rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid; transition: all 0.25s ease; height: 100%; }
                .kpi-charte:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0; }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte { font-size: 0.7rem; color: #94a3b8; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value-charte { font-size: 1.35rem; font-weight: 700; margin: 0.3rem 0; color: #1e293b; }
                .filter-card-charte { background: linear-gradient(135deg, #dc2626, #b91c1c); border-radius: 12px; border: none; box-shadow: 0 8px 24px rgba(220,38,38,0.15); }
                .search-box-charte { position: relative; }
                .search-icon-charte { position: absolute; top: 50%; left: 1rem; transform: translateY(-50%); color: var(--danger); font-size: 1rem; z-index: 2; pointer-events: none; }
                .search-input-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem 0.6rem 2.75rem !important; font-size: 0.9rem; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
                .filter-select-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem !important; font-size: 0.9rem; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
                .btn-modern-light-outline { background: rgba(255,255,255,0.15); border: 1.5px solid rgba(255,255,255,0.3); color: white; border-radius: 50px; padding: 0.5rem 1rem; font-size: 0.85rem; font-weight: 500; transition: all 0.2s ease; }
                .btn-modern-light-outline:hover { background: rgba(255,255,255,0.25); color: white; }
                .card-header-gradient { border: none; padding: 1rem 1.5rem; border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; }
                .badge-header-light { background: rgba(255,255,255,0.2); color: white; border: 1px solid rgba(255,255,255,0.3); border-radius: 50px; padding: 0.25rem 0.75rem; font-size: 0.75rem; font-weight: 600; }
                .table-charte thead th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 0.9rem 0.75rem; }
                .table-charte tbody td { padding: 0.9rem 0.75rem; vertical-align: middle; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .reference-badge-charte { display: inline-flex; align-items: center; gap: 4px; background: #f1f5f9; padding: 0.25rem 0.6rem; border-radius: 6px; font-size: 0.7rem; font-family: monospace; color: #475569; }
                .type-badge { display: inline-flex; align-items: center; padding: 0.3rem 0.8rem; border-radius: 50px; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.3px; }
                .expiration-badge { display: inline-flex; align-items: center; gap: 6px; padding: 0.35rem 0.9rem; border-radius: 50px; font-size: 0.72rem; font-weight: 600; }
                .employee-avatar-charte { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg, ${COLORS.danger}15, ${COLORS.warning}15); color: var(--danger); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.85rem; flex-shrink: 0; }
                .action-btn-charte { width: 36px; height: 36px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: white; border: 1.5px solid #e2e8f0; color: #64748b; font-size: 1rem; transition: all 0.2s ease; cursor: pointer; }
                .action-btn-charte:hover { border-color: var(--btn-color); color: var(--btn-color); transform: translateY(-2px); box-shadow: 0 6px 16px rgba(41,90,167,0.15); }
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte { width: 72px; height: 72px; background: linear-gradient(135deg, #f1f5f9, #e2e8f0); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8; }
                .pagination-wrapper-charte { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 1rem; }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link { border-radius: 10px; border: 1.5px solid #e2e8f0; color: #1e293b; padding: 0.5rem 0.9rem; font-weight: 500; font-size: 0.85rem; transition: all 0.2s; }
                .pagination-charte .page-item.active .page-link { background: linear-gradient(135deg, var(--danger), #b91c1c); border-color: var(--danger); color: white; box-shadow: 0 4px 12px rgba(220,38,38,0.3); }
                .pagination-charte .page-item .page-link:hover { background: #f1f5f9; border-color: var(--danger); color: var(--danger); }
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; font-size: 0.85rem; font-weight: 500; color: #4b5563; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-outline:hover { border-color: var(--secondary); color: var(--secondary); background: #f0fbff; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(33,172,205,0.15); }
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte { position: absolute; inset: 0; border-radius: 50%; border: 4px solid #e2e8f0; border-top-color: var(--danger); animation: spin 1s linear infinite; }
                .loading-icon-charte { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: var(--danger); }
                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; animation: slideInRight 0.3s ease-out; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; opacity: 0.8; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}